/**
 * Builds the 0781 `treatmentProvidersDetails` payload from the private
 * (`providerFacility`) and VA (`vaTreatmentFacilities`) facility arrays.
 */

/**
 * Extracts the month and year from a date string in the format YYYY-MM-DD
 * If the date is missing or invalid, returns empty strings
 *
 * @param {string} dateString the date string to parse
 * @returns {object} object containing `treatmentMonth` (MM) and `treatmentYear` (YYYY)
 */
export function extractDateParts(dateString) {
  const match = dateString?.match(/^(\d{4}|\D+)-(\d{2}|\D+)/);
  return {
    treatmentMonth: match && /^\d{2}$/.test(match[2]) ? match[2] : '',
    treatmentYear: match && /^\d{4}$/.test(match[1]) ? match[1] : '',
  };
}

/**
 * Transforms a provider facility object into the required structure
 * Extracts relevant address information and formats it as a comma-separated string
 *
 * @param {object} facility the provider facility object to transform
 * @returns {object} transformed facility with `facilityInfo`, `treatmentMonth`, and `treatmentYear`
 */
export function transformProviderFacility(facility) {
  const facilityInfo = [
    facility.providerFacilityName,
    facility.providerFacilityAddress?.street,
    facility.providerFacilityAddress?.street2,
    facility.providerFacilityAddress?.city,
    facility.providerFacilityAddress?.state,
    facility.providerFacilityAddress?.postalCode,
    facility.providerFacilityAddress?.country,
  ]
    .filter(Boolean)
    .join(', ');

  const { treatmentMonth, treatmentYear } = extractDateParts(
    facility.treatmentDateRange?.from,
  );

  return { facilityInfo, treatmentMonth, treatmentYear };
}

/**
 * Transforms a VA facility object into the required structure
 * Extracts relevant address information and formats it as a comma-separated string
 *
 * @param {object} facility the VA facility object to transform
 * @returns {object} transformed facility with `facilityInfo`, `treatmentMonth`, and `treatmentYear`
 */
export function transformVaFacility(facility) {
  const facilityInfo = [
    facility.treatmentCenterName,
    facility.treatmentCenterAddress?.city,
    facility.treatmentCenterAddress?.state,
    facility.treatmentCenterAddress?.country,
  ]
    .filter(Boolean)
    .join(', ');

  const { treatmentMonth, treatmentYear } = extractDateParts(
    facility.treatmentDateRange?.from,
  );

  return { facilityInfo, treatmentMonth, treatmentYear };
}

/**
 * Transforms provider and VA facilities, keeping only the ones the Veteran
 * tied to a 0781 event (`treatmentLocation0781Related` is true). v1 doesn't
 * always ask, so those facilities drop out; v2 always asks.
 *
 * @param {array} providerFacilities array of provider facility objects to transform
 * @param {array} vaFacilities array of VA facility objects to transform
 * @returns {array} array of transformed facility objects
 */
export function transformTreatmentFacilities(
  providerFacilities = [],
  vaFacilities = [],
) {
  return [
    ...providerFacilities
      .filter(f => f.treatmentLocation0781Related)
      .map(transformProviderFacility),
    ...vaFacilities
      .filter(f => f.treatmentLocation0781Related)
      .map(transformVaFacility),
  ];
}
